/**
 * releases.js
 * Historical Analytics Layer — Release-based provider.
 *
 * Owns: project_forecast_release_summary, forecast_history
 *   (via the existing buildCostControlDataFromRelease() — no new
 *   calculation engine, pure consumption of what already exists).
 *
 * Responsibility: given a project and a period boundary (a date), find
 * the latest committed Forecast Release on or before that date, and
 * extract the frozen EAC / EV / CPI values from it.
 *
 * This file is STATELESS with respect to "what is the current date" —
 * it never computes "today" or "this month". It only resolves Releases
 * relative to whatever date boundaries the caller supplies. Callers
 * (Dashboard, Executive Report, future Analytics) are responsible for
 * building the Period object; this file's responsibility begins only
 * after that period has been defined.
 *
 * No new tables. No duplicated financial logic. Every number returned
 * here is read from forecast_history via the same reconstruction path
 * Cost Control and Executive Report already use for Release mode.
 */
import { supabase } from './supabase'
import { buildCostControlDataFromRelease } from './buildCostControlData'

/**
 * Finds the latest committed Release for a project with
 * forecast_date <= asOfDate.
 *
 * Consumes project_forecast_release_summary — the same shared view
 * ReleaseManagement.jsx and CostControl.jsx's release picker already
 * use, so this can never disagree with them about what a Release's
 * composition or ordering is.
 *
 * @param {string} projectId
 * @param {string} asOfDate - 'YYYY-MM-DD'
 * @returns {Promise<object|null>} the release summary row, or null if
 *   no committed Release exists on or before asOfDate.
 */
async function findLatestReleaseAsOf(projectId, asOfDate) {
  if (!projectId || !asOfDate) return null

  const { data, error } = await supabase
    .from('project_forecast_release_summary')
    .select('release_id, project_id, forecast_date, release_number, direct_snapshot_id, indirect_snapshot_id')
    .eq('project_id', projectId)
    .lte('forecast_date', asOfDate)
    .order('forecast_date', { ascending: false })
    .order('release_number', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('releases.js: findLatestReleaseAsOf failed', error)
    return null
  }
  return data
}

/**
 * Resolves EAC / EV / CPI for a single point in time (a Release found
 * on or before the given date). Returns null values with quality
 * 'unavailable' if no Release exists that early (e.g. project's first
 * month, before any Forecast was ever committed).
 *
 * @param {string} projectId
 * @param {string} asOfDate - 'YYYY-MM-DD'
 */
async function resolvePointInTime(projectId, asOfDate) {
  const release = await findLatestReleaseAsOf(projectId, asOfDate)

  if (!release) {
    return {
      eac: null, ev: null, cpi: null,
      asOf: null,
      quality: 'unavailable',
    }
  }

  try {
    const { grandTotals } = await buildCostControlDataFromRelease({
      id: release.release_id,
      project_id: release.project_id,
      direct_snapshot_id: release.direct_snapshot_id,
      indirect_snapshot_id: release.indirect_snapshot_id,
    })

    return {
      eac: grandTotals?.eac ?? null,
      ev: grandTotals?.earnedValue ?? null,
      cpi: grandTotals?.cpi ?? null,
      asOf: release.forecast_date,
      quality: 'complete',
    }
  } catch (e) {
    // buildCostControlDataFromRelease() throws explicitly on structural
    // inconsistency (ADD SS18) rather than silently degrading — that is
    // the correct behavior for it. Here, in an aggregation context, we
    // surface that as 'partial' rather than crashing the whole history
    // request: one broken historical Release should not prevent showing
    // trend for every other metric.
    console.error('releases.js: resolvePointInTime failed for release', release.release_id, e)
    return {
      eac: null, ev: null, cpi: null,
      asOf: release.forecast_date,
      quality: 'partial',
    }
  }
}

/**
 * Public entry point for this provider. Given a project and a Period
 * (currentPeriodEnd / previousPeriodEnd — both 'YYYY-MM-DD', supplied
 * entirely by the caller), resolves EAC / EV / CPI at both boundaries.
 *
 * Does NOT compute delta/deltaPct here — that belongs to the Aggregator,
 * which owns the unified output contract shared across providers.
 *
 * @param {string} projectId
 * @param {import('./historyAggregator').HistoricalContext} context - only
 *   context.calendar.currentPeriodEnd / previousPeriodEnd are read here.
 *   These are used purely as an upper search bound ("most recent Release
 *   with forecast_date <= this date") — this file never assumes
 *   forecast_date itself equals a month-end date.
 * @returns {Promise<{
 *   eac:  { current: number|null, previous: number|null, asOfCurrent: string|null, asOfPrevious: string|null, quality: string },
 *   ev:   { current: number|null, previous: number|null, asOfCurrent: string|null, asOfPrevious: string|null, quality: string },
 *   cpi:  { current: number|null, previous: number|null, asOfCurrent: string|null, asOfPrevious: string|null, quality: string },
 * }>}
 */
export async function getReleaseHistory(projectId, context) {
  const currentPeriodEnd = context?.calendar?.currentPeriodEnd
  const previousPeriodEnd = context?.calendar?.previousPeriodEnd
  if (!projectId || !currentPeriodEnd || !previousPeriodEnd) {
    const empty = { current: null, previous: null, asOfCurrent: null, asOfPrevious: null, quality: 'unavailable' }
    return { eac: empty, ev: { ...empty }, cpi: { ...empty } }
  }

  const [currentPoint, previousPoint] = await Promise.all([
    resolvePointInTime(projectId, currentPeriodEnd),
    resolvePointInTime(projectId, previousPeriodEnd),
  ])

  // A metric's overall quality reflects the weaker of its two points —
  // e.g. if the current Release resolved cleanly but the previous one
  // doesn't exist yet (project's first reporting period), the metric
  // as a whole is 'partial': one side is real, the other has no basis.
  const combineQuality = (a, b) => {
    if (a === 'unavailable' && b === 'unavailable') return 'unavailable'
    if (a === 'complete' && b === 'complete') return 'complete'
    return 'partial'
  }

  const buildMetric = (key) => ({
    current: currentPoint[key],
    previous: previousPoint[key],
    asOfCurrent: currentPoint.asOf,
    asOfPrevious: previousPoint.asOf,
    quality: combineQuality(currentPoint.quality, previousPoint.quality),
  })

  return {
    eac: buildMetric('eac'),
    ev: buildMetric('ev'),
    cpi: buildMetric('cpi'),
  }
}
